import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  MotionConfig,
  useInView,
  useReducedMotion,
} from "framer-motion";
import {
  FaBookOpen,
  FaSmile,
  FaUsers,
  FaProjectDiagram,
  FaChild,
  FaGlobe,
  FaShapes,
  FaHandsHelping,
  FaHeart,
  FaShieldAlt,
  FaUniversalAccess,
  FaStar,
  FaFlask,
  FaLightbulb,
  FaComments,
  FaArrowRight,
  FaCheckCircle,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaLanguage,
  FaCalculator,
  FaMosque,
  FaGlobeAsia,
  FaLaptop,
  FaPalette,
  FaFutbol,
  FaMicrophone,
  FaGift,
  FaBus,
  FaPlus,
  FaMinus,
  FaPaperPlane,
} from "react-icons/fa";

import "./PrimarySchool.css";

/* Change this if your admission route is different (routes are case-sensitive) */
const APPLY_PATH = "/ApplyForm";

/* ------------------------------------------------------------------ */
/* Data (outside component => not re-created on each render)           */
/* ------------------------------------------------------------------ */
const highlights = [
  { icon: <FaChild />, title: "Child-Centred", text: "Learning that respects every child's pace" },
  { icon: <FaChalkboardTeacher />, title: "Caring Teachers", text: "Guidance and encouragement at every step" },
  { icon: <FaProjectDiagram />, title: "Activity-Based", text: "Hands-on learning beyond the textbook" },
  { icon: <FaShieldAlt />, title: "Safe Campus", text: "A secure, respectful school environment" },
];

const learningFeatures = [
  { icon: <FaFlask />, title: "STEM Education", color: "blue", description: "Introduce children to Science, Technology, Engineering and Mathematics through exploration, practical activities and age-appropriate problem-solving." },
  { icon: <FaProjectDiagram />, title: "Project-Based Learning", color: "orange", description: "Encourage students to connect classroom knowledge with real-life situations through creative projects, teamwork and discovery." },
  { icon: <FaSmile />, title: "S.M.I.L.E.S Approach", color: "green", description: "Support sensory, motor, intellectual, linguistic, emotional and social development through meaningful learning experiences." },
  { icon: <FaHeart />, title: "PSHE Education", color: "pink", description: "Develop self-awareness, positive relationships, healthy habits, empathy and emotional well-being." },
  { icon: <FaGlobe />, title: "Inquiry-Based Learning", color: "purple", description: "Build curiosity by encouraging children to ask questions, explore themes, discover ideas and understand the world around them." },
  { icon: <FaShapes />, title: "Learning Through Play", color: "yellow", description: "Make early learning engaging through hands-on experiences, visual activities, creative expression and guided exploration." },
];

const subjects = [
  { icon: <FaBookOpen />, title: "English", text: "Phonics, reading, writing and confident spoken English." },
  { icon: <FaLanguage />, title: "Urdu", text: "Strong reading, writing and expression in the national language." },
  { icon: <FaCalculator />, title: "Mathematics", text: "Number sense, operations, shapes and everyday problem-solving." },
  { icon: <FaFlask />, title: "Science", text: "Observation, simple experiments and curiosity about nature." },
  { icon: <FaMosque />, title: "Islamiyat", text: "Good manners, values and basic teachings taught with understanding." },
  { icon: <FaGlobeAsia />, title: "Social Studies", text: "Community, culture, geography and responsible citizenship." },
  { icon: <FaLaptop />, title: "Computer Studies", text: "Basic digital skills and safe, responsible use of technology." },
  { icon: <FaPalette />, title: "Art & Craft", text: "Colour, drawing and making things with their own hands." },
];

const developmentAreas = [
  { number: "01", title: "Language & Communication", description: "Build vocabulary, listening skills, reading habits and the confidence to express ideas." },
  { number: "02", title: "Mathematical Thinking", description: "Develop number sense, counting, patterns, logical reasoning and everyday problem-solving." },
  { number: "03", title: "Creativity & Discovery", description: "Explore ideas through art, storytelling, observation, experiments and creative activities." },
  { number: "04", title: "Social & Personal Growth", description: "Learn cooperation, responsibility, self-confidence, kindness and respect for others." },
];

const daySteps = [
  { title: "Welcome & Assembly", text: "The day begins together with greetings, recitation and a positive message." },
  { title: "Core Lessons", text: "English, Urdu and Mathematics taught through clear, structured teaching." },
  { title: "Activity & Discovery", text: "Group tasks, simple experiments and creative projects bring lessons to life." },
  { title: "Break & Play", text: "Time to eat, rest, make friends and move around in a supervised space." },
  { title: "Skills Session", text: "Computer, art, library and sports sessions through the week." },
  { title: "Review & Home Connection", text: "Revision, light practice work and notes that keep parents informed." },
];

const schoolValues = [
  { icon: <FaChild />, title: "Child-Centred Learning", description: "Every child learns differently. We encourage individual progress, participation, confidence and curiosity." },
  { icon: <FaShieldAlt />, title: "Safe Learning Environment", description: "We value a welcoming and respectful school environment where children can learn, participate and grow." },
  { icon: <FaUniversalAccess />, title: "Inclusive Education", description: "We promote respect for individual differences and encourage every learner to participate in school life." },
  { icon: <FaHandsHelping />, title: "Parent Partnership", description: "Positive communication between parents and teachers helps support children's learning and development." },
];

const activities = [
  { icon: <FaFutbol />, title: "Sports & Games", text: "Physical activity builds health, teamwork and healthy competition." },
  { icon: <FaPalette />, title: "Art & Craft Displays", text: "Children showcase their creativity and take pride in their work." },
  { icon: <FaMicrophone />, title: "Speech & Storytelling", text: "Regular chances to speak, present and share ideas with confidence." },
  { icon: <FaFlask />, title: "Science Corner", text: "Simple experiments and exhibitions that make science exciting." },
  { icon: <FaGift />, title: "Celebrations & Events", text: "Special days and school events that build community and memories." },
  { icon: <FaBus />, title: "Educational Trips", text: "Learning outside the classroom through guided and safe visits." },
];

const parentPoints = [
  "Regular progress updates",
  "Parent–teacher meetings",
  "Clear communication with teachers",
  "Guidance for learning at home",
];

const admissionSteps = [
  { title: "Inquire", text: "Visit the school or contact the admission office to learn about the programme." },
  { title: "Apply", text: "Fill in the online application form with your child's and your details." },
  { title: "Meet & Assess", text: "A friendly interaction helps us place your child in the right grade." },
  { title: "Confirm", text: "Complete the required documents and admission formalities." },
  { title: "Start Learning", text: "Your child joins the class and begins an exciting new journey." },
];

const faqs = [
  { q: "Which grades does the primary section cover?", a: "Our primary programme supports children from the early primary grades onwards. Please contact the admission office to confirm the exact grades and age requirements for the current session." },
  { q: "How can I apply for admission?", a: "Click “Apply Now”, fill in the online form and submit it. Our admission team will contact you with the next steps." },
  { q: "How will I know how my child is progressing?", a: "Through regular classroom assessments, teacher feedback and parent–teacher meetings, so you always know how your child is doing." },
  { q: "Is homework given in primary classes?", a: "Yes, light and age-appropriate practice that reinforces what was taught, without overloading young learners." },
  { q: "How do you keep children safe at school?", a: "Classrooms and breaks are supervised, we promote respectful behaviour, and there are clear guidelines for arrival and dismissal." },
];

/* ------------------------------------------------------------------ */
/* Animation helpers                                                   */
/* ------------------------------------------------------------------ */
const EASE = [0.22, 1, 0.36, 1];
const VIEW = { once: true, amount: 0.15 };

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};
const pop = {
  hidden: { opacity: 0, scale: 0.92 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: EASE } },
};
const slideLeft = {
  hidden: { opacity: 0, x: -30 },
  show: { opacity: 1, x: 0, transition: { duration: 0.55, ease: EASE } },
};
const stagger = (gap = 0.1) => ({
  hidden: {},
  show: { transition: { staggerChildren: gap } },
});

/* Centered section heading */
const SectionHeading = ({ label, title, highlight, text }) => (
  <motion.header
    className="ags-primary-section-heading"
    variants={fade}
    initial="hidden"
    whileInView="show"
    viewport={VIEW}
  >
    <span className="ags-primary-section-label">{label}</span>
    <h2>
      {title}
      {highlight && <span> {highlight}</span>}
    </h2>
    {text && <p>{text}</p>}
  </motion.header>
);

/* Staggered grid. Each child sits inside <Cell> so framer's inline transform
   never fights with the CSS :hover transform of the card. */
const Grid = ({ className = "", children, gap = 0.09 }) => (
  <motion.div
    className={className}
    variants={stagger(gap)}
    initial="hidden"
    whileInView="show"
    viewport={VIEW}
  >
    {children}
  </motion.div>
);
const Cell = ({ children, variants = fade }) => (
  <motion.div className="ags-primary-cell" variants={variants}>
    {children}
  </motion.div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
const PrimarySchool = () => {
  const reduce = useReducedMotion();
  const [openFaq, setOpenFaq] = useState(null);

  const heroRef = useRef(null);
  const closingRef = useRef(null);
  const heroInView = useInView(heroRef, { amount: 0.1 });
  const closingInView = useInView(closingRef, { amount: 0.2 });
  const showFab = !heroInView && !closingInView;

  const goTo = (e, id) => {
    e.preventDefault();
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  return (
    <MotionConfig reducedMotion="user">
      <main className="ags-primary">
        {/* ================= Hero ================= */}
        <section className="ags-primary-hero" ref={heroRef}>
          <div className="ags-primary-container ags-primary-hero-inner">
            <motion.div
              className="ags-primary-hero-content"
              variants={stagger(0.14)}
              initial="hidden"
              animate="show"
            >
              <motion.span className="ags-primary-eyebrow" variants={fade}>
                ACADEMIC GROUP OF SCHOOLS & COLLEGE
              </motion.span>

              <motion.h1 variants={fade}>
                Primary Education
                <span> Building Strong Foundations for Life</span>
              </motion.h1>

              <motion.p variants={fade}>
                Every great journey begins with a strong foundation. At Academic
                Group of Schools & College (AGS), we encourage children to
                discover their potential, develop essential skills and grow into
                confident, curious and responsible learners.
              </motion.p>

              <motion.div className="ags-primary-hero-actions" variants={fade}>
                <Link to={APPLY_PATH} className="ags-primary-btn">
                  Apply Now <FaArrowRight />
                </Link>
                <a
                  href="#primary-learning"
                  onClick={(e) => goTo(e, "primary-learning")}
                  className="ags-primary-btn ags-primary-btn-outline"
                >
                  Explore Our Approach
                </a>
              </motion.div>

              <motion.div className="ags-primary-hero-note" variants={fade}>
                <FaCheckCircle />
                <span>Learning, character development and curiosity at every step.</span>
              </motion.div>
            </motion.div>

            <motion.div
              className="ags-primary-hero-visual"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.25 }}
            >
              <div className="ags-primary-illustration">
                <div className="ags-primary-illustration-icon">
                  <FaGraduationCap />
                </div>
                <span className="ags-primary-illustration-label">LEARN · GROW · ACHIEVE</span>
                <h2>Little Steps.</h2>
                <h2>Big Possibilities.</h2>
                <p>
                  Inspiring young minds through knowledge, creativity and
                  meaningful experiences.
                </p>
                <div className="ags-primary-illustration-footer">
                  <span><FaBookOpen /> Knowledge</span>
                  <span><FaLightbulb /> Discovery</span>
                </div>
              </div>

              <div className="ags-primary-floating-card">
                <FaStar />
                <div>
                  <strong>A Brighter Future</strong>
                  <span>One lesson at a time</span>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="ags-primary-hero-decoration" />
        </section>

        {/* ================= Highlights strip ================= */}
        <Grid className="ags-primary-container ags-primary-highlights" gap={0.1}>
          {highlights.map((h) => (
            <motion.div className="ags-primary-highlight" key={h.title} variants={pop}>
              <span className="ags-primary-highlight-icon">{h.icon}</span>
              <div>
                <strong>{h.title}</strong>
                <span>{h.text}</span>
              </div>
            </motion.div>
          ))}
        </Grid>

        {/* ================= Introduction ================= */}
        <section className="ags-primary-intro ags-primary-section" id="primary-programme">
          <div className="ags-primary-container ags-primary-intro-grid">
            <motion.div
              className="ags-primary-intro-heading"
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={VIEW}
            >
              <span className="ags-primary-section-label">OUR PRIMARY PROGRAMME</span>
              <h2>
                Where Young Minds
                <span> Begin to Flourish</span>
              </h2>
              <motion.div
                className="ags-primary-heading-line"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
              />
            </motion.div>

            <Grid className="ags-primary-intro-copy" gap={0.15}>
              <motion.p variants={fade}>
                Primary education plays an important role in shaping a child's
                academic journey. At AGS, we believe that children learn best
                when they feel encouraged to ask questions, explore ideas and
                take an active part in the learning process.
              </motion.p>
              <motion.p variants={fade}>
                Our primary learning approach focuses on foundational literacy,
                numeracy, communication, creativity and social development.
                Through classroom activities, guided exploration and
                collaborative learning, we aim to help students develop the
                confidence and skills needed for future academic success.
              </motion.p>
              <motion.p variants={fade}>
                We strive to create meaningful learning experiences that nurture
                curiosity, encourage independent thinking and help children
                appreciate the value of knowledge.
              </motion.p>
            </Grid>
          </div>
        </section>

        {/* ================= Learning approach ================= */}
        <section
          className="ags-primary-learning ags-primary-section ags-primary-alt"
          id="primary-learning"
        >
          <div className="ags-primary-container">
            <SectionHeading
              label="HOW WE SUPPORT LEARNING"
              title="A Thoughtful Approach to"
              highlight="Primary Education"
              text="We encourage active participation, practical understanding and a love of learning through a variety of educational approaches."
            />

            <Grid className="ags-primary-feature-grid">
              {learningFeatures.map((feature, index) => (
                <Cell key={feature.title}>
                  <article className={`ags-primary-feature-card ags-primary-${feature.color}`}>
                    <div className="ags-primary-feature-top">
                      <div className="ags-primary-feature-icon">{feature.icon}</div>
                      <span className="ags-primary-card-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                    <div className="ags-primary-card-bottom">
                      <span>Learning through discovery</span>
                      <FaArrowRight />
                    </div>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Subjects ================= */}
        <section className="ags-primary-section">
          <div className="ags-primary-container">
            <SectionHeading
              label="WHAT CHILDREN LEARN"
              title="A Balanced Curriculum for"
              highlight="Growing Minds"
              text="Core subjects build strong academic skills, while creative and practical subjects help children discover their talents."
            />

            <Grid className="ags-primary-subject-grid">
              {subjects.map((s) => (
                <Cell key={s.title} variants={pop}>
                  <article className="ags-primary-subject-card">
                    <div className="ags-primary-subject-icon">{s.icon}</div>
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Development ================= */}
        <section className="ags-primary-development ags-primary-section ags-primary-alt">
          <div className="ags-primary-container ags-primary-development-grid">
            <motion.div
              className="ags-primary-development-copy"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={VIEW}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <span className="ags-primary-section-label">GROWING WITH CONFIDENCE</span>
              <h2>
                Strong Foundations for
                <span> Lifelong Learning</span>
              </h2>
              <p>
                Early childhood and primary school years are important stages of
                discovery and development. Our approach encourages children to
                build essential academic skills while developing independence,
                communication and positive learning habits.
              </p>
              <p>
                As children progress through primary grades, learning activities
                can gradually introduce more complex ideas, helping students
                strengthen their understanding and prepare for the next stage of
                education.
              </p>
              <div className="ags-primary-development-highlight">
                <FaBookOpen />
                <div>
                  <strong>Learning with Purpose</strong>
                  <p>
                    Encouraging children to understand, practise, question and
                    apply what they learn.
                  </p>
                </div>
              </div>
            </motion.div>

            <Grid className="ags-primary-development-list" gap={0.12}>
              {developmentAreas.map((area) => (
                <motion.article
                  className="ags-primary-development-item"
                  key={area.number}
                  variants={fade}
                >
                  <span className="ags-primary-development-number">{area.number}</span>
                  <div>
                    <h3>{area.title}</h3>
                    <p>{area.description}</p>
                  </div>
                </motion.article>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= A day in primary (sequence) ================= */}
        <section className="ags-primary-section">
          <div className="ags-primary-container">
            <SectionHeading
              label="A DAY AT SCHOOL"
              title="A Day in Our"
              highlight="Primary Classroom"
              text="A predictable, joyful routine helps young children feel secure and ready to learn."
            />

            <div className="ags-primary-day">
              <motion.span
                className="ags-primary-day-line"
                aria-hidden="true"
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 1.6, ease: "easeOut" }}
              />
              <motion.ol
                className="ags-primary-day-list"
                variants={stagger(0.15)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.1 }}
              >
                {daySteps.map((step, i) => (
                  <motion.li className="ags-primary-day-item" key={step.title} variants={slideLeft}>
                    <span className="ags-primary-day-marker">{i + 1}</span>
                    <div className="ags-primary-day-card">
                      <h3>{step.title}</h3>
                      <p>{step.text}</p>
                    </div>
                  </motion.li>
                ))}
              </motion.ol>
            </div>
          </div>
        </section>

        {/* ================= Values ================= */}
        <section className="ags-primary-values ags-primary-section ags-primary-alt">
          <div className="ags-primary-container">
            <SectionHeading
              label="OUR EDUCATIONAL VALUES"
              title="Every Child Deserves"
              highlight="the Opportunity to Thrive"
              text="A positive school experience supports children's academic progress, personal development and relationships."
            />

            <Grid className="ags-primary-values-grid">
              {schoolValues.map((value) => (
                <Cell key={value.title}>
                  <article className="ags-primary-value-card">
                    <div className="ags-primary-value-icon">{value.icon}</div>
                    <h3>{value.title}</h3>
                    <p>{value.description}</p>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Grades 1 & 2 ================= */}
        <section className="ags-primary-grades ags-primary-section">
          <div className="ags-primary-container">
            <motion.div
              className="ags-primary-grades-panel"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={VIEW}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <div className="ags-primary-grades-content">
                <span className="ags-primary-section-label">THE EARLY PRIMARY YEARS</span>
                <h2>
                  Grades 1 & 2
                  <span> The Beginning of Discovery</span>
                </h2>
                <p>
                  The early primary years help children strengthen the skills
                  they need for a successful school journey. Through structured
                  lessons, age-appropriate activities and teacher guidance,
                  students can develop foundational reading, writing,
                  mathematics and communication skills.
                </p>
                <p>
                  We encourage children to participate, share their ideas, work
                  with classmates and approach new challenges with confidence
                  and curiosity.
                </p>
                <div className="ags-primary-grades-points">
                  <span><FaCheckCircle /> Foundational literacy</span>
                  <span><FaCheckCircle /> Numeracy and reasoning</span>
                  <span><FaCheckCircle /> Creativity and teamwork</span>
                  <span><FaCheckCircle /> Positive learning habits</span>
                </div>
              </div>

              <div className="ags-primary-grades-art">
                <div className="ags-primary-grade-circle ags-primary-grade-circle-one">
                  <FaBookOpen />
                  <strong>GRADE 1</strong>
                  <span>Explore</span>
                </div>
                <div className="ags-primary-grade-circle ags-primary-grade-circle-two">
                  <FaLightbulb />
                  <strong>GRADE 2</strong>
                  <span>Discover</span>
                </div>
                <div className="ags-primary-grade-art-caption">
                  Every new skill builds confidence.
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ================= Beyond the classroom ================= */}
        <section className="ags-primary-section ags-primary-alt">
          <div className="ags-primary-container">
            <SectionHeading
              label="BEYOND THE CLASSROOM"
              title="Learning That Goes"
              highlight="Beyond Books"
              text="Activities and events help children build confidence, friendships and a love for school."
            />

            <Grid className="ags-primary-activity-grid">
              {activities.map((a) => (
                <Cell key={a.title} variants={pop}>
                  <article className="ags-primary-activity-card">
                    <span className="ags-primary-activity-icon">{a.icon}</span>
                    <div>
                      <h3>{a.title}</h3>
                      <p>{a.text}</p>
                    </div>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Parents ================= */}
        <section className="ags-primary-parents ags-primary-section">
          <div className="ags-primary-container">
            <motion.div
              className="ags-primary-parents-inner"
              variants={stagger(0.12)}
              initial="hidden"
              whileInView="show"
              viewport={VIEW}
            >
              <motion.div className="ags-primary-parents-icon" variants={pop}>
                <FaUsers />
              </motion.div>

              <motion.div className="ags-primary-parents-copy" variants={fade}>
                <span className="ags-primary-section-label">WORKING TOGETHER</span>
                <h2>
                  A Partnership Between
                  <span> School and Home</span>
                </h2>
                <p>
                  Parents and teachers play valuable roles in a child's
                  education. Open communication, encouragement at home and
                  collaboration with teachers can help children develop
                  confidence, positive habits and a stronger connection with
                  learning.
                </p>
                <ul className="ags-primary-parents-list">
                  {parentPoints.map((p) => (
                    <li key={p}><FaCheckCircle /> {p}</li>
                  ))}
                </ul>
              </motion.div>

              <motion.div className="ags-primary-parents-note" variants={fade}>
                <FaComments />
                <span>Together, we can make every learning experience more meaningful.</span>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ================= Admission process ================= */}
        <section className="ags-primary-process ags-primary-section ags-primary-alt" id="primary-admission">
          <div className="ags-primary-container">
            <SectionHeading
              label="ADMISSION PROCESS"
              title="Joining Our School Is"
              highlight="Simple and Easy"
              text="Five clear steps from your first inquiry to your child's first day."
            />

            <Grid className="ags-primary-process-grid" gap={0.12}>
              {admissionSteps.map((s, i) => (
                <Cell key={s.title} variants={pop}>
                  <article className="ags-primary-step">
                    <span className="ags-primary-step-marker">{i + 1}</span>
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                  </article>
                </Cell>
              ))}
            </Grid>

            <motion.div
              className="ags-primary-process-cta"
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={VIEW}
            >
              <Link to={APPLY_PATH} className="ags-primary-btn">
                Start Your Application <FaArrowRight />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ================= FAQ ================= */}
        <section className="ags-primary-section">
          <div className="ags-primary-container ags-primary-narrow">
            <SectionHeading
              label="PARENTS' QUESTIONS"
              title="Frequently Asked"
              highlight="Questions"
            />

            <Grid className="ags-primary-faq" gap={0.08}>
              {faqs.map((f, i) => {
                const open = openFaq === i;
                return (
                  <motion.div
                    key={f.q}
                    variants={fade}
                    className={`ags-primary-faq-item ${open ? "is-open" : ""}`}
                  >
                    <button
                      type="button"
                      id={`pr-faq-btn-${i}`}
                      aria-expanded={open}
                      aria-controls={`pr-faq-${i}`}
                      onClick={() => setOpenFaq(open ? null : i)}
                    >
                      <span>{f.q}</span>
                      {open ? <FaMinus aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
                    </button>
                    <div
                      id={`pr-faq-${i}`}
                      className="ags-primary-faq-body"
                      role="region"
                      aria-labelledby={`pr-faq-btn-${i}`}
                    >
                      <div><p>{f.a}</p></div>
                    </div>
                  </motion.div>
                );
              })}
            </Grid>
          </div>
        </section>

        {/* ================= Closing CTA ================= */}
        <section className="ags-primary-closing" ref={closingRef}>
          <motion.div
            className="ags-primary-container ags-primary-closing-inner"
            variants={stagger(0.12)}
            initial="hidden"
            whileInView="show"
            viewport={VIEW}
          >
            <motion.div className="ags-primary-closing-icon" variants={pop}>
              <FaGraduationCap />
            </motion.div>

            <motion.span variants={fade}>ACADEMIC GROUP OF SCHOOLS & COLLEGE</motion.span>

            <motion.h2 variants={fade}>
              Inspiring Young Minds.
              <br />
              Preparing Them for Tomorrow.
            </motion.h2>

            <motion.p variants={fade}>
              We believe that every child has the potential to learn, grow and
              achieve. By nurturing curiosity, building strong foundations and
              encouraging positive values, we aim to make primary education a
              meaningful step towards lifelong learning.
            </motion.p>

            <motion.div variants={fade}>
              <Link to={APPLY_PATH} className="ags-primary-btn">
                Apply Now <FaArrowRight />
              </Link>
            </motion.div>
          </motion.div>
        </section>

        {/* ================= Floating apply button ================= */}
        <AnimatePresence>
          {showFab && (
            <motion.div
              className="ags-primary-fab"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.4, duration: 0.4 } }}
              exit={{ opacity: 0, y: 24, transition: { duration: 0.2 } }}
            >
              <Link to={APPLY_PATH} aria-label="Apply for admission">
                <FaPaperPlane aria-hidden="true" /> Apply Now
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </MotionConfig>
  );
};

export default PrimarySchool;
